import { Box, Divider, Grid, Paper, Typography } from "@material-ui/core";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import {
    IApplicantHistoryProps,
    useGetSizeByHistoryType
} from "../ApplicantHistoryHelper";
import ApplicantHistoryItemDetail from "./ApplicantHistoryItemDetail";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { useAuthState } from "_ReactContext/AuthContext";
import _ from "lodash";
import { useQueryClient } from "react-query";
import { useGlobalDialog } from "_ReactContext/GlobalDialogContext";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { apiDirectCall } from "customHooks/useApi";
import useApplicantViewContext from "../../useApplicantViewContext";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            display: "flex",
            padding: theme.spacing(2),
            height: "100%",
            flexDirection: "column",
            minWidth: "200px", // Ensure consistent minimum width
            // Responsive minimum widths
            [theme.breakpoints.down("sm")]: {
                minWidth: "180px",
                padding: theme.spacing(1.5)
            },
            [theme.breakpoints.down("xs")]: {
                minWidth: "150px",
                padding: theme.spacing(1)
            }
        },
        divider: {
            marginTop: theme.spacing(1)
        },
        comment: {
            textAlign: "left",
            overflow: "auto"
        },
        grow: {
            flexGrow: 1
        },
        viewIcon: {
            margin: theme.spacing(0.1),
            padding: theme.spacing(0.1)
        }
    })
);

function createMarkup(data: string) {
    return { __html: data };
}

const ApplicantHistoryComment = (props: IApplicantHistoryProps) => {
    const classes = useStyles();
    const { historyItem, tLocal, language, setModalCommentOpen } = props;
    const [data] = useApplicantViewContext();

    const { user } = useAuthState();
    const isCommentFromCurrentUser =
        historyItem.createdByUser === user &&
        !_.isEmpty(historyItem.createdByUser);
    const historyType = historyItem.type as CompleoShared.Common.ApplicantHistoryType;
    const allowedEditTypes: CompleoShared.Common.ApplicantHistoryType[] = [
        "addComment",
        "interview",
        "phoneCall"
    ];
    const id = (historyItem.skGS1pk || "").split(":")[1];
    const isEditable =
        allowedEditTypes.includes(historyType) && isCommentFromCurrentUser;
    const queryClient = useQueryClient();
    const { callDialog } = useGlobalDialog();
    const showMessage = useShowMessage();

    const handleDelete = () => {
        callDialog({
            title: tLocal("modalTitleDelete"),
            bodyText: tLocal("deleteConfirmation"),
            agreeFunction: async () => {
                const postAddress = `/applicant/deleteapplicanthistorycomment`;
                try {
                    const dataAPI = await apiDirectCall(postAddress, "post", {
                        id: id,
                        applicantId: data?.applicantId,
                        companyId: data?.companyId
                    });
                    if (dataAPI.status === 200) {
                        showMessage(tLocal("deleteSuccessMessage"), "success");
                        queryClient.invalidateQueries(
                            "/applicant/applicanthistorylist"
                        );
                    } else {
                        showMessage(tLocal("NotIdentifiedProblem"), "error");
                    }
                } catch (error) {
                    console.error("Error deleting history item:", error);
                    showMessage(tLocal("NotIdentifiedProblem"), "error");
                }
            },
            agreeButtonText: tLocal("COMPLEOGENERAL_Delete"),
            disagreeButtonText: tLocal("COMPLEOGENERAL_CANCEL")
        });
    };
    const { historySizeType } = useGetSizeByHistoryType();
    const isCompact = historySizeType === "compact";

    return (
        <Paper elevation={1} className={classes.paper}>
            <Grid container>
                <Grid item xs={isCompact ? 4 : 12}>
                    <ApplicantHistoryItemDetail
                        title={tLocal("Type")}
                        transformDBDataParam={{
                            value: tLocal(
                                `ApplicantHistoryType_${historyItem?.type}`
                            ),
                            t: tLocal,
                            language: language
                        }}
                    />
                </Grid>
                <Grid item xs={isCompact ? 4 : 12}>
                    {historyItem.jobData && (
                        <ApplicantHistoryItemDetail
                            title={tLocal("JobTitle")}
                            transformDBDataParam={{
                                value: historyItem.jobData?.title,
                                language: language,
                                t: tLocal
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={isCompact ? 4 : 12}>
                    {historyItem?.stage?.name && (
                        <ApplicantHistoryItemDetail
                            title={tLocal("Stage")}
                            transformDBDataParam={{
                                value: historyItem?.stage?.name,
                                language: language,
                                t: tLocal
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            <Typography variant="body2" className={classes.comment}>
                <div
                    dangerouslySetInnerHTML={createMarkup(historyItem.comment)}
                />
            </Typography>
            <div className={classes.grow} />
            <Divider className={classes.divider} />
            <Box display={"flex"}>
                <Typography variant="caption">
                    {historyItem.createdByUser}
                </Typography>
                {isEditable && (
                    <>
                        <Box flexGrow={1} />
                        <ButtonIconTooltipCompleo
                            label={tLocal("COMPLEOGENERAL_Delete")}
                            onClick={() => {
                                handleDelete();
                            }}
                            className={classes.viewIcon}
                        >
                            <DeleteIcon className={classes.viewIcon} />
                        </ButtonIconTooltipCompleo>
                        <ButtonIconTooltipCompleo
                            label={tLocal("COMPLEOGENERAL_Edit")}
                            onClick={() => {
                                queryClient.invalidateQueries(
                                    "/applicant/applicanthistorylist"
                                );
                                setModalCommentOpen({
                                    open: true,
                                    editData: {
                                        commentId: id,
                                        historyData: historyItem
                                    }
                                });
                            }}
                            className={classes.viewIcon}
                        >
                            <EditIcon className={classes.viewIcon} />
                        </ButtonIconTooltipCompleo>
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default ApplicantHistoryComment;
